"use client"

import type { ArtistWithArtworks } from "#app/(frontend)/[locale]/(public)/artists/_components/artistOverview.service"
import type { SupportedLocales } from "#environment/i18nConfig"

import React from "react"

import { Trans } from "#modules/localization/trans.server"
import { useTranslation } from "#modules/localization/translations.client"
import { OImage } from "#modules/optimization/media/image"
import SvgArrowRight from "#svgs/semantic icons/ArrowRight"
import { Link } from "#utils/localization/clientUtils"

export function ArtistCard({ artistWithArtworks: { artist, artworks }, locale }: { artistWithArtworks: ArtistWithArtworks; locale: SupportedLocales }) {
  const { t, i18n } = useTranslation("common", "page/artistOverview")

  return (
    <Link href={`/artists/${artist.slug}`} locale={locale}>
      <article
        className={
          "mb-7 grid cursor-pointer grid-cols-1 rounded-3xl bg-white p-7 shadow-[0px_3px_57px_0px_rgba(0,0,0,0.15)] transition-all hover:scale-105 sm:mb-12 sm:grid-cols-[repeat(3,auto)] sm:gap-x-7 sm:p-8 xl:gap-x-11 xl:p-9 xl:pb-10 xl:pl-8"
        }>
        <OImage
          media={artist.profilePicture}
          lazy={true}
          placeholder={"blur"}
          className={"col-start-1 row-start-1 size-15 rounded-full object-cover sm:row-span-4 sm:size-20 sm:min-w-20 xl:size-28 xl:min-w-28"}
          sizes={"(min-width: 1280px) 112px, (min-width: 640px) 80px, 60px"}
        />
        <ul
          className={
            "col-start-1 row-start-1 my-auto ml-auto flex w-fit flex-col items-end gap-y-2 text-[10px] sm:col-start-2 sm:row-start-2 sm:mb-0 sm:ml-0 sm:mt-3.5 sm:flex-row sm:gap-x-4 xl:mt-4 xl:text-[12px]"
          }>
          {/*@ts-ignore*/}
          {artist.tags.slice(0, 2).map((tag: string) => (
            <li key={tag} className={"whitespace-nowrap rounded-md bg-gold-100 px-2 py-1 sm:px-4 xl:px-5"}>
              {tag}
            </li>
          ))}
        </ul>
        <h2 className={"mt-3.5 text-[25px] font-bold -tracking-[0.51px] sm:col-start-2 sm:mt-0 sm:text-[26px] xl:-mt-1 xl:text-ih4"}>{artist.fullName}</h2>
        <p className={"mt-3.5 line-clamp-3 text-[13px] sm:col-start-2 sm:line-clamp-2 sm:text-nm xl:mt-7 xl:text-bg"}>{artist.biography}</p>
        <p className={"mt-5 hidden text-nm text-gray-50 sm:col-start-2 sm:block xl:text-bg"}>
          <Trans
            i18n={i18n}
            i18nKey={"page/artistOverview:bornIn"}
            t={t}
            values={{ YEAR: artist.birthYear, COUNTRY: artist.birthCountry }}
            components={{
              highlight: <span className={"text-[#515151]"} />,
            }}
          />
        </p>
        {artworks.length > 0 && (
          <footer
            className={
              "mt-6 flex flex-wrap gap-x-3 sm:col-start-3 sm:row-span-5 sm:row-start-1 sm:mt-0 sm:min-w-10 sm:flex-col sm:gap-y-2 md:min-w-11 xl:mt-1.5 xl:flex-row xl:content-start xl:justify-end xl:gap-x-2"
            }>
            {artworks.map(artwork => (
              // @ts-ignore
              <OImage
                key={artwork.id}
                media={artwork.previewImages[0]}
                lazy={true}
                placeholder={"blur"}
                className={"size-10 rounded-md object-cover md:size-11 xl:size-14"}
                sizes={"(min-width: 1280px) 56px, (min-width: 768px) 44px, 40px"}
              />
            ))}
            <p className={"-mr-3 mt-2 h-fit text-[13px] sm:mt-0 sm:text-[12px] xl:mr-0 xl:w-full xl:text-end xl:text-nm"}>
              <span className={"hidden sm:inline-block"}>{t("generic.more")}</span>...
            </p>
          </footer>
        )}
        <button
          className={
            "mt-8 flex w-fit items-center rounded-lg bg-black py-2 pl-9 pr-7 text-[13px] font-bold text-white transition-all hover:scale-105 hover:bg-gold-100 hover:text-black sm:col-start-2 sm:mt-5 xl:col-start-3 xl:row-span-2 xl:row-start-3 xl:ml-auto xl:mt-auto xl:h-fit xl:py-2.5 xl:pl-11 xl:pr-8 xl:text-bg"
          }>
          {t("artist.viewProfile")}
          <SvgArrowRight className={"ml-0.5 size-4.5 xl:ml-1 xl:size-6"} />
        </button>
      </article>
    </Link>
  )
}
